import type { Web3ProviderInterface, Address } from "@/types";
import { AppError } from "@/types";
import { safeAsync } from "@/utils";
import { useAccount, useConnect, useDisconnect, useSwitchChain } from "wagmi";
import { useCallback } from "react";
import { useENSName, useSavedWallet } from "@/hooks";
import { SUPPORTED_CHAIN_IDS } from "@/config";

export const useWalletConnection = (): Web3ProviderInterface & {
    ensName: string | null;
    isENSLoading: boolean;
    switchNetwork: (chainId: number) => Promise<void>;
} => {
    const { address, isConnected, isConnecting } = useAccount();
    const { connectAsync, connectors } = useConnect();
    const { disconnectAsync } = useDisconnect();
    const { ensName, isLoading: isENSLoading } = useENSName(address);
    const { clearSavedWallet } = useSavedWallet();
    const { switchChainAsync } = useSwitchChain();

    const connect = async (): Promise<void> => {
        if (connectors.length === 0) {
            throw new AppError("No connectors available");
        }

        const { error } = await safeAsync(() => connectAsync({ connector: connectors[0] }));
        if (error) {
            throw new AppError("Failed to connect wallet", { error });
        }
    };

    const disconnect = useCallback(async (): Promise<void> => {
        const { error } = await safeAsync(() => disconnectAsync());
        if (error) {
            throw new AppError("Failed to disconnect wallet", { error });
        }
        // Clear saved wallet when disconnecting
        clearSavedWallet();
    }, [disconnectAsync, clearSavedWallet]);

    const switchNetwork = useCallback(
        async (targetChainId: number): Promise<void> => {
            if (!SUPPORTED_CHAIN_IDS.includes(targetChainId)) {
                throw new AppError(`Chain ID ${targetChainId} is not supported`);
            }

            try {
                await switchChainAsync({ chainId: targetChainId });
            } catch (error) {
                throw new AppError("Failed to switch network", { error });
            }
        },
        [switchChainAsync],
    );

    return {
        connect,
        disconnect,
        isConnected,
        isConnecting,
        address: address as Address,
        ensName,
        isENSLoading,
        switchNetwork,
    };
};
